import { supabase } from "@/integrations/supabase/client";
import type { Database, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { hostPayment, type HostPayment, type HostPaymentRow } from "@shared/hostPayment";

/** Every location with the columns the admin location/event editors need. */
export function listLocationsForEdit() {
  return supabase
    .from("v_location")
    .select("id, location_name, city, country, latitude, longitude, location_link, route_link, timezone, default_currency")
    .order("city");
}

export function insertLocations(rows: TablesInsert<"location">[]) {
  return supabase
    .from("location")
    .insert(rows)
    .select("id");
}

/** Atomic create + self-assign as host (non-admin hosts). */
export function hostCreateLocation(
  args: Database["public"]["Functions"]["host_create_location"]["Args"],
) {
  return supabase.rpc("host_create_location", args);
}

export function updateLocationRow(id: number, payload: TablesUpdate<"location">) {
  return supabase.from("location").update(payload).eq("id", id);
}

export function deleteLocations(ids: number[]) {
  return supabase.from("location").delete().in("id", ids);
}

/** Distinct-able city/country pairs from v_location. */
export function listLocationCities() {
  return supabase
    .from("v_location")
    .select("city, country")
    .order("city");
}

/** Cities hosting onsite events (v_onsite_city). */
export function listOnsiteCities() {
  return supabase
    .from("v_onsite_city")
    .select("city, country")
    .order("city");
}

/** Resolve a short maps URL via the `resolve-url` edge function. */
export function resolveUrl(url: string) {
  return supabase.functions.invoke("resolve-url", {
    body: { url },
  });
}

/** Host payment details per location_id (RLS: admin, or the location's hosts). */
export async function listLocationPayments(): Promise<Map<number, HostPaymentRow>> {
  const { data, error } = await supabase
    .from("location_payment")
    .select("location_id, pay_url, pay_note");
  if (error) throw error;
  return new Map(data.map((r) => [r.location_id, { pay_url: r.pay_url, pay_note: r.pay_note }]));
}

/** Save a location's host payment details; both empty removes them. */
export async function saveLocationPayment(locationId: number, row: HostPaymentRow) {
  const pay_url = row.pay_url?.trim() || null;
  const pay_note = row.pay_note?.trim() || null;
  const { error } = pay_url || pay_note
    ? await supabase
        .from("location_payment")
        .upsert({ location_id: locationId, pay_url, pay_note }, { onConflict: "location_id" })
    : await supabase.from("location_payment").delete().eq("location_id", locationId);
  if (error) throw error;
}

/**
 * Where to pay for an event: the host's details, or null to pay us (Stripe).
 * The RPC only answers registered participants and the location's hosts.
 */
export async function getEventPaymentInfo(eventId: number): Promise<HostPayment | null> {
  const { data, error } = await supabase.rpc("get_event_payment_info", { p_event_id: eventId });
  if (error) throw error;
  return hostPayment(data?.[0]);
}
